import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Box, InputField, Typography, color } from '@kis-technologies/kis-ui';
import { ActionContainer } from 'components/ActionContainer';
import { useActions } from 'hooks/useActions';
import { selectors as authSelectors, actions as authActions } from 'store/auth';
import { validateEmail } from 'utils/validation';
import { useStyles } from './styled';

const ForgotPasswordView: React.FC = () => {
  const { requestResetPassword } = useActions({ requestResetPassword: authActions.requestResetPassword });

  const error = useSelector(authSelectors.selectActionError);
  const updating = useSelector(authSelectors.selectActionPending);
  const resetSuccess = useSelector(authSelectors.selectActionSuccessful);

  const [email, setEmail] = useState('');
  const [invalidMsg, setInvalidMsg] = useState<string | null>(null);

  const classes = useStyles();

  // ON UPDATE OF ERROR - SET THE ERROR AS INVALID MESSAGE
  useEffect(() => {
    if (!updating) {
      setInvalidMsg(error);
    }
  }, [error, updating]);

  // ***************
  // ** FUNCTIONS **
  // ***************

  // EMAIL VALIDATION
  const emailValidationFn = async (value: string | number | undefined) => {
    // VALIDATION SCHEMA
    const schema = validateEmail();

    // VALIDATE FN
    const result = await schema.validate(value).catch((err) => err);

    // IF THERE WAS ERROR MESSAGE - SET IT TO INVALID MESSAGE
    setInvalidMsg(result && result.message);
  };

  // INPUT CHANGE
  const onInputChange = (setFn: React.Dispatch<React.SetStateAction<string>>, value: string) => {
    // IF INVALID MESSAGE EXISTS SET IT TO NULL
    if (invalidMsg) setInvalidMsg(null);

    // FIRES THE APPROPRIATE FN: SETPASSWORD | SETCONFIRMPASSWORD WITH VALUE
    setFn(value);
  };

  // FORM SUBMIT
  const onSaveClick = (e: React.SyntheticEvent<HTMLButtonElement>) => {
    e.preventDefault();

    requestResetPassword(email);
  };

  const buttonDisabled = !email.length || !!invalidMsg;

  return (
    <ActionContainer
      buttonAction={resetSuccess ? undefined : onSaveClick}
      buttonDisabled={buttonDisabled}
      header="Forgot Password"
      isLoading={updating}
    >
      <Box>
        {!resetSuccess ? (
          <>
            {/* INSTRUCTIONS */}
            <Box className={classes.instructionsBox}>
              <Typography>
                Enter the email address associated with your account. We will send an email with a link to reset your
                password.
              </Typography>
            </Box>
            {/* EMAIL INPUT */}
            <Box className={classes.inputFieldBox}>
              <InputField
                onChange={(e) => onInputChange(setEmail, e.target.value)}
                error={!!invalidMsg}
                helperText={emailValidationFn}
                label="Email"
                type="email"
                placeholder="Your Email"
                size="small"
                sx={{ width: { xs: '262px', sm: '524px' } }}
              />
            </Box>
            {/* ERROR MESSAGE */}
            <Box>{invalidMsg && !updating && <Typography color={color.RED}>{invalidMsg}</Typography>}</Box>
          </>
        ) : (
          <>
            {/* INSTRUCTIONS */}
            <Box className={classes.instructionsBox}>
              <Typography>
                Please check your inbox. An email has been sent containing a link to reset your password. You can now
                close this tab and follow the instructions found in the email.
              </Typography>
            </Box>
          </>
        )}
      </Box>
    </ActionContainer>
  );
};

export default ForgotPasswordView;
